import { useSessionPreference } from '../../Hooks/useSessionPreference';
import { useTranslation } from 'react-i18next';
import { BizStaffingCalendar } from '../Mini/BizStaffingCalendar';
import { localDay, getMonthRange } from '../../Utils/calendarUtils';
import { useBizQuery } from '../../Hooks/useBizQuery';
import { getPersonalStaffingCalendar } from '../../Services/staffing';
import { staffingCalendarKind } from '../../Utils/staffingPeople';
import './EventMonthCalendar.scss';
import { Event, EventStatus } from '../../Types/event';

export default function EventMonthCalendar({
	onOpen,
	preferenceKey,
	staffingEnabled = false,
	listEvents = [],
	loading = false,
}: {
	onOpen: (id: number) => void;
	preferenceKey?: string;
	staffingEnabled?: boolean;
	listEvents?: Pick<Event, 'Id' | 'Name' | 'StartDateTimeUtc' | 'EndDateTimeUtc' | 'Status'>[];
	loading?: boolean;
}) {
	const { t, i18n } = useTranslation();
	const labels = {
		mine: t('STAFFING_CALENDAR.REGULAR', { defaultValue: 'Ordinarie' }),
		substitute: t('STAFFING_CALENDAR.SUBSTITUTE', { defaultValue: 'Inhopp' }),
		absent: t('STAFFING_CALENDAR.ABSENT', { defaultValue: 'Frånvaro' }),
		available: t('STAFFING_CALENDAR.RELEASED', { defaultValue: 'Släppt till inhoppare' }),
	};
	const [month, setMonth] = useSessionPreference(preferenceKey, localDay(new Date()).slice(0, 7), (value) =>
		/^\d{4}-(0[1-9]|1[0-2])$/.test(value)
	);
	const { from, to } = getMonthRange(month);
	const date = new Date(`${month}-01T12:00:00`);
	const moveMonth = (delta: number) => {
		const next = new Date(date);
		next.setMonth(next.getMonth() + delta);
		setMonth(localDay(next).slice(0, 7));
	};
	const query = useBizQuery(['STAFFING', 'calendar', from, to], () => getPersonalStaffingCalendar(from, to), staffingEnabled);
	const events = staffingEnabled
		? (query.data ?? []).flatMap((event) => {
				const kind = staffingCalendarKind(event);
				if (!kind) return [];
				return [
					{ ...event, kind: kind === 'other' ? undefined : kind, label: kind === 'other' ? undefined : labels[kind] },
				];
			})
		: listEvents.map((event) => ({
				id: event.Id,
				name: event.Name,
				start: new Date(event.StartDateTimeUtc).toISOString(),
				end: new Date(event.EndDateTimeUtc).toISOString(),
				isCancelled: event.Status === EventStatus.Cancelled,
			}));
	const isLoading = staffingEnabled ? query.isFetching : loading;
	return (
		<div className='biz-event-month-calendar'>
			<div className='biz-event-month-calendar__toolbar'>
				<div className='biz-event-month-calendar__navigation'>
					<button
						type='button'
						aria-label={t('STAFFING_CALENDAR.PREVIOUS_MONTH', { defaultValue: 'Föregående månad' })}
						onClick={() => moveMonth(-1)}
					>
						←
					</button>
					<button
						type='button'
						aria-label={t('STAFFING_CALENDAR.NEXT_MONTH', { defaultValue: 'Nästa månad' })}
						onClick={() => moveMonth(1)}
					>
						→
					</button>
					<strong aria-live='polite'>
						{date.toLocaleDateString(i18n.resolvedLanguage || 'sv-SE', { month: 'long', year: 'numeric' })}
					</strong>
					<span className={`biz-event-month-calendar__loading${isLoading ? ' is-loading' : ''}`} role='status'>
						{t('STAFFING_CALENDAR.LOADING', { defaultValue: 'Laddar...' })}
					</span>
				</div>
				{staffingEnabled && (
					<div
						className='biz-event-month-calendar__legend'
						aria-label={t('STAFFING_CALENDAR.TYPES', { defaultValue: 'Passtyper' })}
					>
						{Object.entries(labels).map(([kind, label]) => (
							<span key={kind} className={`is-${kind}`}>
								{label}
							</span>
						))}
					</div>
				)}
			</div>
			{staffingEnabled && query.errorMessage && <p role='alert'>{query.errorMessage}</p>}
			<BizStaffingCalendar from={from} to={to} month={month} events={events} onOpen={onOpen} />
		</div>
	);
}
